import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
  alpha,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

// Icons
import StorefrontIcon from "@mui/icons-material/Storefront";
import BusinessIcon from "@mui/icons-material/Business";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FactoryIcon from "@mui/icons-material/Factory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InventoryIcon from "@mui/icons-material/Inventory";

// Components
import InputField from "../../../FormComponents/InputField";
import Ex_ProductSelectingPopup from "../../../components/Ex_ProductSelectingPopup";
import SaleBillItemForm from "../../Sales/SaleBill/SaleBillItemForm";
import FormAlert from "../../../components/FormAlert";
import WarehouseSelectingPopup from "../../../components/WarehouseSelectingPopup";

// Services
import { addWarehouseTransferBill } from "../../../services/WarehouseTransfer";
import { getExportProductById } from "../../../services/Export";
import { getWarehouseData, getWarehouseById } from "../../../services/Warehouse";
import { getSettingsByCode } from "../../../services/Settings";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  billTypeCard: {
    padding: theme.spacing(4),
    cursor: "pointer",
    transition: "all 0.3s ease",
    border: "2px solid transparent",
    height: "100%",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
    },
  },
  selectedCard: {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
  },
  warehouseCard: {
    padding: theme.spacing(2.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "2px solid transparent",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
    },
  },
  selectedWarehouseCard: {
    borderColor: theme.palette.success.main,
    backgroundColor: alpha(theme.palette.success.main, 0.05),
  },
  iconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(3),
  },
}));

const getWarehouseIcon = (warehouseType, size = 32) => {
  if (warehouseType === "Factory") {
    return <FactoryIcon sx={{ fontSize: size }} />;
  }
  return <LocalShippingIcon sx={{ fontSize: size }} />;
};

function WarehouseTransferAddBill({ setOpenForm }) {
  const classes = useStyles();

  // Step control
  const [billType, setBillType] = useState("");
  const [showSourceWarehouseStep, setShowSourceWarehouseStep] = useState(false);
  const [showBillForm, setShowBillForm] = useState(false);

  // Source warehouse (Step 2)
  const [warehouses, setWarehouses] = useState([]);
  const [warehousesLoading, setWarehousesLoading] = useState(false);
  const [selectedSourceWarehouse, setSelectedSourceWarehouse] = useState(null);

  // Destination warehouse (Step 3 - inside form)
  const [destinationWarehouseIds, setDestinationWarehouseIds] = useState([]);
  const [selectedDestinationWarehouse, setSelectedDestinationWarehouse] = useState(null);
  const [openDestinationPopup, setOpenDestinationPopup] = useState(false);

  // Bill values
  const [billNumber, setBillNumber] = useState("");
  const [autoIncrementing, setAutoIncrementing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [billDate, setBillDate] = useState(null);
  const [note, setNote] = useState("");

  // Product management
  const [productRows, setProductRows] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentProductRow, setCurrentProductRow] = useState(null);
  const [openProductPopup, setOpenProductPopup] = useState(false);
  const [openProductProcess, setOpenProductProcess] = useState(false);

  // Alert / loading
  const [displayAlert, setAlert] = useState(false);
  const [alertData, setAlertData] = useState({ type: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);

  // Load warehouses when entering Step 2
  useEffect(() => {
    if (showSourceWarehouseStep) {
      loadWarehouses();
    }
  }, [showSourceWarehouseStep]);

  // Load bill number from settings on mount
  useEffect(() => {
    const loadBillNumber = async () => {
      try {
        const settings = await getSettingsByCode("warehouseTransfer");
        if (settings && settings.data) {
          const isAuto = !!settings.data.autoIncrementing;
          setAutoIncrementing(isAuto);
          if (isAuto && settings.data.nextBillNumber !== undefined) {
            setBillNumber(String(settings.data.nextBillNumber));
          }
        }
      } catch (e) {
        // settings not critical
      }
    };
    loadBillNumber();
  }, []);

  const loadWarehouses = async () => {
    setWarehousesLoading(true);
    const result = await getWarehouseData(0, 100, {
      _name: "",
      _type: "",
      _isActive: "true",
    });
    if (result && result !== "Something went wrong..." && result.content) {
      setWarehouses(result.content);
    }
    setWarehousesLoading(false);
  };

  // ── Navigation ───────────────────────────────────────────────────────────────

  const handleBillTypeSelect = (type) => {
    setBillType(type);
    setShowSourceWarehouseStep(true);
  };

  const handleSourceWarehouseSelect = (warehouse) => {
    setSelectedSourceWarehouse(warehouse);
  };

  const handleSourceWarehouseContinue = () => {
    if (!selectedSourceWarehouse) return;
    setShowSourceWarehouseStep(false);
    setShowBillForm(true);
  };

  const handleBack = () => {
    if (showBillForm) {
      setShowBillForm(false);
      setShowSourceWarehouseStep(true);
      return;
    }
    if (showSourceWarehouseStep) {
      setShowSourceWarehouseStep(false);
      setBillType("");
      setSelectedSourceWarehouse(null);
      return;
    }
    setOpenForm(false);
  };

  const resetToStart = () => {
    setBillType("");
    setShowSourceWarehouseStep(false);
    setShowBillForm(false);
    setSelectedSourceWarehouse(null);
    setSelectedDestinationWarehouse(null);
    setDestinationWarehouseIds([]);
    setProductRows([]);
    setSelectedProducts([]);
    setNote("");
    setSelectedDate(null);
    setBillDate(null);
  };

  // ── Destination warehouse popup handlers ─────────────────────────────────────

  const handleCloseDestinationPopup = async () => {
    if (destinationWarehouseIds.length !== 0) {
      const result = await getWarehouseById(destinationWarehouseIds[0]);
      if (result && result !== "Something went wrong...") {
        setSelectedDestinationWarehouse(result);
      }
    }
    setOpenDestinationPopup(false);
  };

  // ── Product popup handlers ────────────────────────────────────────────────────

  const handleCloseProductPopup = async () => {
    if (selectedProducts.length === 0) {
      setOpenProductPopup(false);
      return;
    }

    const newRows = [];
    for (const productId of selectedProducts) {
      if (productRows.find((r) => r.productId === productId)) continue;
      try {
        const data = await getExportProductById(productId);
        if (data && data !== "Something went wrong...") {
          newRows.push({
            productId: data.product_id,
            productName: data.name,
            barcode: data.barcode,
            sellingQty: 0,
            unitPrice: 0,
            totalPrice: 0,
            totalCost: 0,
            totalProfit: 0,
            profitMargin: 0,
            batches: [],
          });
        }
      } catch (e) {
        showAlert("error", `Failed to load product data for ID: ${productId}`);
      }
    }

    if (newRows.length > 0) {
      setProductRows((prev) => [...prev, ...newRows]);
    }
    setOpenProductPopup(false);
  };

  const handleOpenProductProcess = (product) => {
    setCurrentProductRow(product);
    setOpenProductProcess(true);
  };

  const handleRemoveProduct = (productId) => {
    setProductRows((prev) => prev.filter((r) => r.productId !== productId));
  };

  // ── Alert helpers ─────────────────────────────────────────────────────────────

  const showAlert = (type, message) => {
    setAlertData({ type, message });
    setAlert(true);
  };

  const closeAlert = () => {
    setAlert(false);
    setAlertData({ type: "", message: "" });
  };

  // ── Totals ────────────────────────────────────────────────────────────────────

  const calculateTotals = () => {
    const totalAmount = productRows.reduce((sum, r) => sum + (r.totalPrice || 0), 0);
    const totalProfit = productRows.reduce((sum, r) => sum + parseFloat(r.totalProfit || 0), 0);
    const totalCost = productRows.reduce((sum, r) => sum + parseFloat(r.totalCost || 0), 0);
    return { totalAmount, totalProfit, totalCost };
  };

  // ── Validation ────────────────────────────────────────────────────────────────

  const validateBill = () => {
    if (!selectedDestinationWarehouse) {
      showAlert("error", "Please select a destination warehouse");
      return false;
    }
    if (productRows.length === 0) {
      showAlert("error", "Please add at least one product to the bill");
      return false;
    }
    const pending = productRows.filter(
      (r) => !r.sellingQty || r.sellingQty <= 0 || !r.batches.length
    );
    if (pending.length > 0) {
      showAlert("error", "Please configure all products with valid quantities and production batches");
      return false;
    }
    return true;
  };

  // ── Submit ────────────────────────────────────────────────────────────────────

  const handleSaveBill = async () => {
    if (!validateBill()) return;

    setIsLoading(true);
    closeAlert();

    try {
      const { totalAmount, totalProfit } = calculateTotals();

      const values = {
        _transferredById: null,
        _totalPrice: totalAmount,
        _totalProfit: totalProfit,
        _billDate: billDate,
        _billNumber: billNumber,
        _note: note,
        _sourceWarehouse: selectedSourceWarehouse.warehouseId,
        _destinationWarehouse: selectedDestinationWarehouse.warehouseId,
        _billCategory: billType,
      };

      const billItems = productRows.map((row) => ({
        productId: row.productId,
        bill_qty: row.sellingQty,
        discount_perItem: row.discountAmount || 0,
        pricePerItem: row.unitPrice,
        price: row.totalPrice,
        itemProfit: row.totalProfit,
        profitMargin: row.profitMargin,
        costPerItem: row.totalCost / row.sellingQty,
        note: "",
        saleProductionItems: row.batches.map((batch) => ({
          usingQty: batch.saleQty,
          productionId: batch.productionId,
        })),
      }));

      const result = await addWarehouseTransferBill(values, billItems);
      if (result) {
        showAlert("success", "Warehouse transfer bill created successfully as DRAFT!");
        setTimeout(() => setOpenForm(false), 2000);
      } else {
        showAlert("error", "Failed to save bill. Please try again.");
      }
    } catch (e) {
      showAlert("error", "An error occurred while saving the bill.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;
  const getBillTypeColor = () => (billType === "RETAIL" ? "primary" : "secondary");
  const getBillTypeIcon = () =>
    billType === "RETAIL" ? <StorefrontIcon fontSize="large" /> : <BusinessIcon fontSize="large" />;

  // ── Step 1: Bill Type Selection ───────────────────────────────────────────────

  if (!showSourceWarehouseStep && !showBillForm) {
    return (
      <Paper className={classes.container} elevation={3}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => setOpenForm(false)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Create Warehouse Transfer Bill
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose the transfer type
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card
              className={`${classes.billTypeCard} ${billType === "RETAIL" ? classes.selectedCard : ""}`}
              onClick={() => handleBillTypeSelect("RETAIL")}
              elevation={billType === "RETAIL" ? 8 : 2}
            >
              <CardContent>
                <Box className={classes.iconContainer}>
                  <StorefrontIcon
                    sx={{ fontSize: 100, color: billType === "RETAIL" ? "primary.main" : "text.secondary" }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  align="center"
                  fontWeight="700"
                  gutterBottom
                  color={billType === "RETAIL" ? "primary.main" : "text.primary"}
                >
                  Retail Transfer
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" paragraph>
                  Transfer products for retail pricing
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Stack spacing={1.5}>
                  {["Retail priced transfer", "Single unit pricing", "Factory to Lorry / Lorry to Lorry"].map(
                    (text) => (
                      <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "primary.main" }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    )
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              className={`${classes.billTypeCard} ${billType === "WHOLESALE" ? classes.selectedCard : ""}`}
              onClick={() => handleBillTypeSelect("WHOLESALE")}
              elevation={billType === "WHOLESALE" ? 8 : 2}
            >
              <CardContent>
                <Box className={classes.iconContainer}>
                  <BusinessIcon
                    sx={{ fontSize: 100, color: billType === "WHOLESALE" ? "secondary.main" : "text.secondary" }}
                  />
                </Box>
                <Typography
                  variant="h4"
                  align="center"
                  fontWeight="700"
                  gutterBottom
                  color={billType === "WHOLESALE" ? "secondary.main" : "text.primary"}
                >
                  Wholesale Transfer
                </Typography>
                <Typography variant="body1" color="text.secondary" align="center" paragraph>
                  Transfer products for wholesale pricing
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Stack spacing={1.5}>
                  {["Wholesale priced transfer", "Bulk quantity pricing", "Factory to Lorry / Lorry to Lorry"].map(
                    (text) => (
                      <Box key={text} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "secondary.main" }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    )
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setOpenForm(false)}>
            Cancel
          </Button>
        </Box>
      </Paper>
    );
  }

  // ── Step 2: Source Warehouse Selection ────────────────────────────────────────

  if (showSourceWarehouseStep) {
    return (
      <Paper className={classes.container} elevation={3}>
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={handleBack} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" fontWeight="600" color="primary.main">
                Select Source Warehouse
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose the warehouse products will be transferred FROM
              </Typography>
            </Box>
            <Chip label={billType} size="small" color={getBillTypeColor()} />
          </Stack>
        </Box>

        <Divider sx={{ mb: 4 }} />

        {warehousesLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : warehouses.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <WarehouseIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              No active warehouses found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {warehouses.map((warehouse) => {
              const isSelected = selectedSourceWarehouse?.warehouseId === warehouse.warehouseId;
              return (
                <Grid item xs={12} sm={6} md={4} key={warehouse.warehouseId}>
                  <Card
                    className={`${classes.warehouseCard} ${isSelected ? classes.selectedWarehouseCard : ""}`}
                    onClick={() => handleSourceWarehouseSelect(warehouse)}
                    elevation={isSelected ? 6 : 2}
                  >
                    <CardContent>
                      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                        <Box sx={{ color: isSelected ? "success.main" : "text.secondary" }}>
                          {getWarehouseIcon(warehouse.warehouseType, 32)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            color={isSelected ? "success.main" : "text.primary"}
                          >
                            {warehouse.warehouseName}
                          </Typography>
                          {warehouse.warehouseType && (
                            <Chip label={warehouse.warehouseType} size="small" variant="outlined" sx={{ mt: 0.5 }} />
                          )}
                        </Box>
                        {isSelected && <Chip label="Selected" size="small" color="success" />}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={!selectedSourceWarehouse}
            onClick={handleSourceWarehouseContinue}
          >
            Continue
          </Button>
        </Box>
      </Paper>
    );
  }

  // ── Step 3: Bill Form ─────────────────────────────────────────────────────────

  const { totalAmount, totalProfit, totalCost } = calculateTotals();

  return (
    <Paper className={classes.container} elevation={3}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton onClick={handleBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ color: `${getBillTypeColor()}.main` }}>{getBillTypeIcon()}</Box>
          <SwapHorizIcon color="action" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="600" color={`${getBillTypeColor()}.main`}>
              {billType === "RETAIL" ? "Retail" : "Wholesale"} Warehouse Transfer Bill
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip label={billType} size="small" color={getBillTypeColor()} />
              <Chip
                icon={getWarehouseIcon(selectedSourceWarehouse?.warehouseType, 14)}
                label={`From: ${selectedSourceWarehouse?.warehouseName}`}
                size="small"
                color="success"
                variant="outlined"
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {displayAlert && (
        <Box sx={{ mb: 3 }}>
          <FormAlert type={alertData.type} message={alertData.message} />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Destination Warehouse */}
        <Grid item xs={12} md={4} lg={4}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
            Destination Warehouse
          </Typography>
          {selectedDestinationWarehouse ? (
            <Card
              elevation={2}
              sx={{
                cursor: "pointer",
                border: "2px solid",
                borderColor: "success.main",
                bgcolor: alpha("#4caf50", 0.05),
                transition: "all 0.2s",
                "&:hover": { boxShadow: 4 },
              }}
              onClick={() => setOpenDestinationPopup(true)}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box sx={{ color: "success.main" }}>
                    {getWarehouseIcon(selectedDestinationWarehouse.warehouseType, 32)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight="600" color="success.main">
                      {selectedDestinationWarehouse.warehouseName}
                    </Typography>
                    <Chip
                      label={selectedDestinationWarehouse.warehouseType}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  <Tooltip title="Change destination">
                    <IconButton size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Card
              elevation={2}
              sx={{
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": { boxShadow: 4, transform: "translateY(-2px)" },
              }}
              onClick={() => setOpenDestinationPopup(true)}
            >
              <CardContent sx={{ textAlign: "center", py: 4 }}>
                <WarehouseIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  Click to Select Destination
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Bill Info */}
        <Grid item xs={12} md={8}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
            Bill Information
          </Typography>
          <Card elevation={2}>
            <CardContent>
              <Stack spacing={2}>
                <InputField
                  name="_billNumber"
                  value={billNumber}
                  onChange={(name, val) => setBillNumber(val)}
                  type="text"
                  label={autoIncrementing ? "Bill Number (auto)" : "Bill Number"}
                  isdisabled={autoIncrementing}
                />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    size="small"
                    label="Bill Date (Optional)"
                    value={selectedDate}
                    onChange={(date) => {
                      setSelectedDate(date);
                      setBillDate(date ? date.toDate() : null);
                    }}
                    slotProps={{ textField: { size: "small", fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Bill Summary */}
        {productRows.length > 0 && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
              Bill Summary
            </Typography>
            <Card elevation={2} sx={{ bgcolor: alpha("#4caf50", 0.05) }}>
              <CardContent>
                <Stack spacing={2}>
                  {[
                    { label: "Total Cost", value: totalCost, color: "inherit" },
                    { label: "Total Amount", value: totalAmount, color: "primary.main" },
                    { label: "Total Profit", value: totalProfit, color: "success.main" },
                  ].map(({ label, value, color }) => (
                    <React.Fragment key={label}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                        <Typography variant="h6" fontWeight="600" color={color}>
                          {formatCurrency(value)}
                        </Typography>
                      </Box>
                      <Divider />
                    </React.Fragment>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Products Section */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  Products ({productRows.length})
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<InventoryIcon />}
                  onClick={() => setOpenProductPopup(true)}
                  size="small"
                  color={getBillTypeColor()}
                >
                  Add Products
                </Button>
              </Box>

              {productRows.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{ bgcolor: alpha(billType === "RETAIL" ? "#1976d2" : "#9c27b0", 0.1) }}
                      >
                        <TableCell>
                          <strong>Product</strong>
                        </TableCell>
                        <TableCell>
                          <strong>Barcode</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Qty</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Unit Price</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Profit</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Status</strong>
                        </TableCell>
                        <TableCell align="center">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productRows.map((row) => (
                        <TableRow
                          key={row.productId}
                          sx={{
                            "&:hover": {
                              bgcolor: alpha(billType === "RETAIL" ? "#1976d2" : "#9c27b0", 0.05),
                            },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {row.productName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={row.barcode} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            {row.sellingQty > 0 ? (
                              <Chip label={row.sellingQty} size="small" color={getBillTypeColor()} />
                            ) : (
                              <Chip label="Not set" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {row.unitPrice > 0 ? formatCurrency(row.unitPrice) : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="600">
                              {row.totalPrice > 0 ? formatCurrency(row.totalPrice) : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {row.totalProfit > 0 ? (
                              <Typography variant="body2" fontWeight="600" color="success.main">
                                {formatCurrency(row.totalProfit)}
                              </Typography>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {row.sellingQty > 0 && row.batches.length > 0 ? (
                              <Chip label="Ready" size="small" color="success" />
                            ) : (
                              <Chip label="Pending" size="small" color="warning" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Configure">
                              <IconButton
                                size="small"
                                color={getBillTypeColor()}
                                onClick={() => handleOpenProductProcess(row)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveProduct(row.productId)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 6,
                    bgcolor: alpha("#000", 0.02),
                    borderRadius: 2,
                  }}
                >
                  <InventoryIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    No products added yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Click "Add Products" to begin — products are loaded from the selected source warehouse
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" fontWeight="600" gutterBottom sx={{ mb: 1.5 }}>
            Additional Notes (Optional)
          </Typography>
          <InputField
            name="_note"
            value={note}
            onChange={(name, val) => setNote(val)}
            type="text"
            placeholder="Add any special instructions or notes for this transfer..."
            multiline
            rows={3}
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Divider sx={{ mb: 3 }} />
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={handleBack} disabled={isLoading}>
              Back
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveBill}
              disabled={
                isLoading ||
                productRows.length === 0 ||
                !selectedDestinationWarehouse
              }
              color={getBillTypeColor()}
            >
              {isLoading ? "Saving..." : "Save as Draft"}
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {/* Destination Warehouse Popup */}
      {openDestinationPopup && (
        <WarehouseSelectingPopup
          setOpenWarehouseTable={setOpenDestinationPopup}
          openWarehouseTable={openDestinationPopup}
          handleCloseWarehouseTable={handleCloseDestinationPopup}
          setSelectedWarehouse={setDestinationWarehouseIds}
          selectedWarehouse={destinationWarehouseIds}
          skipList={[selectedSourceWarehouse?.warehouseId].filter(Boolean)}
          isOneChoise={true}
        />
      )}

      {/* Product Popup */}
      {openProductPopup && (
        <Ex_ProductSelectingPopup
          setOpenProductTable={setOpenProductPopup}
          openProductTable={openProductPopup}
          handleCloseProductTable={handleCloseProductPopup}
          setSelectedProduct={setSelectedProducts}
          selectedProduct={selectedProducts}
          filterWarehouse={selectedSourceWarehouse}
          isOneChoise={false}
        />
      )}

      {/* Product Configure Form */}
      {openProductProcess && currentProductRow && (
        <SaleBillItemForm
          productRow={currentProductRow}
          setProductRows={setProductRows}
          handleOpenProductProcess={() => setOpenProductProcess(false)}
          billType={billType}
        />
      )}
    </Paper>
  );
}

export default WarehouseTransferAddBill;
